'use client';

import { useTranslations } from 'next-intl';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { GoalsEvaluation } from '@/types/project';

// Per-variant notice palette (surface / border / text), mirrored from the shared
// NoticeBanner so the model's brief feedback reads as the same family — but
// icon-less. The variant is server-driven (goals_evaluation_variant): a workable
// brief reads as reassuring teal, only a weak one stays amber.
type FeedbackVariant = 'warning' | 'info' | 'success';

const VARIANT_TOKENS: Record<
  FeedbackVariant,
  { surface: string; border: string; text: string }
> = {
  warning: {
    surface: 'bg-[#FBFBE4] dark:bg-amber-900/20',
    border: 'border-[rgba(217,119,6,0.18)] dark:border-[rgba(180,83,9,0.45)]',
    text: 'text-[#594D33] dark:text-amber-400',
  },
  // info / success share the teal palette (matches NoticeBanner).
  info: {
    surface: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'border-[rgba(7,141,134,0.18)] dark:border-[rgba(15,118,110,0.45)]',
    text: 'text-[#2D3A3A] dark:text-teal-400',
  },
  success: {
    surface: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'border-[rgba(7,141,134,0.18)] dark:border-[rgba(15,118,110,0.45)]',
    text: 'text-[#2D3A3A] dark:text-teal-400',
  },
};

/**
 * Map the 1..3 quality score to the notice palette — the same client-side
 * score→presentation mapping the signal-bar indicator already does. A workable
 * brief reads as reassuring teal, only a weak one stays amber:
 *   1 → warning (amber), 2 → info (teal), 3 → success (teal).
 * Unknown/null (pending or never evaluated) falls back to warning.
 */
function variantForScore(score?: number | null): FeedbackVariant {
  if (score === 3) return 'success';
  if (score === 2) return 'info';
  return 'warning';
}

interface GoalsEvaluationFeedbackProps {
  evaluation?: GoalsEvaluation | null;
  pending?: boolean;
  /** 1..3 quality score — drives the card's severity palette (see
   *  variantForScore). */
  score?: number | null;
}

/**
 * Server-side evaluation of the whole brief — text AND photos — shown verbatim
 * from the model, directly under the brief input it comments on. Rendered as a
 * NoticeBanner-family card whose severity follows the quality score (weak →
 * amber warning, workable → teal info/success) but with NO leading icon. Texts
 * come verbatim from the API (already localized + tone-shaped). Shared shape
 * with Apple/Android.
 */
export function GoalsEvaluationFeedback({
  evaluation,
  pending,
  score,
}: GoalsEvaluationFeedbackProps) {
  const t = useTranslations('project');
  // Pending has no settled score yet — keep the neutral warning tone until the
  // evaluation lands, then color by score.
  const tokens = VARIANT_TOKENS[pending ? 'warning' : variantForScore(score)];

  if (pending) {
    return (
      <ModelSpeechCard icon={Loader2} spin tokens={tokens}>
        <p className={cn('text-card-meta leading-[1.3]', tokens.text)}>
          {t('goalsEvaluating')}
        </p>
      </ModelSpeechCard>
    );
  }

  const suggestions = evaluation?.suggestions ?? [];
  if (suggestions.length === 0) return null;

  return (
    <ModelSpeechCard tokens={tokens}>
      {suggestions.map((suggestion, i) => (
        <p key={i} className={cn('text-card-meta leading-[1.3]', tokens.text)}>
          {suggestion}
        </p>
      ))}
    </ModelSpeechCard>
  );
}

/**
 * The model's brief feedback as a NoticeBanner-family card (score-driven
 * severity palette) — but icon-less, so the words carry the tone. Only the
 * transient "evaluating" state shows a spinner.
 */
function ModelSpeechCard({
  icon: Icon,
  spin = false,
  tokens,
  children,
}: {
  icon?: LucideIcon;
  spin?: boolean;
  tokens: { surface: string; border: string; text: string };
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-2xl border p-4', tokens.surface, tokens.border)}>
      <div className="flex items-start gap-2.5">
        {Icon && (
          <Icon
            className={cn(
              'h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400',
              spin && 'animate-spin'
            )}
          />
        )}
        <div className="min-w-0 flex-1 space-y-2">{children}</div>
      </div>
    </div>
  );
}
